import {
  addFlight,
  addReturnFlight,
} from '@/slices/selectedFlight/slices/selectedFlightSlice';
import { style } from '@/styles/globals';
import { useAppDispatch, useAppSelector } from '@/utils/hook';
import { useBoolean } from '@/utils/hooks';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/nextjs';
import { get, isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import { memo, useEffect, useState } from 'react';
import { toVND } from '@/utils/currency';
import { FlightData } from '@/slices/flightData/slices';
import { pr } from '@/utils/pxToRem';
import styled from '@emotion/styled';
import { resetGlobalAll } from '@/slices/global/globalSlice';
import { IVerifyFlightData } from '@/slices/global';
import { useLazyGetFlightByIdQuery } from '@/slices/flightData/apis/flightDataApi';
import { setFlightErrorId } from '@/slices/flightData/slices/flightDataSlice';

const VerifyFlightModal = ({ verifyFlightError }: { verifyFlightError: IVerifyFlightData }) => {
  const dispatch = useAppDispatch();
  const { back } = useRouter();
  const [verifyFlightErrorMsg, setVerifyFlightErrorMsg] = useState<string | undefined>(get(verifyFlightError, 'message'));
  const [getFlightById] = useLazyGetFlightByIdQuery();

  const selectedFlight = useAppSelector((state) => state.selectedFlight);
  const [currFlight, setCurrFlight] = useState<FlightData | null>(null);
  // const verifyFlightError = useAppSelector(
  //   (state) => state.global.verifyFlightError,
  // );
  const [visibleVerifyFlightMessage, setVisibleVerifyFlightMessage] = useBoolean(false);

  useEffect(() => () => {
    dispatch(resetGlobalAll());
  }, []);

  useEffect(() => {
    if (verifyFlightError && !verifyFlightError.message) {
      captureException(verifyFlightError);
    }
  }, [verifyFlightError]);

  // const totalApplicablePassengers = Number(query['passenger[adt]']) + Number(query['passenger[chd]']);
  const passengerSearchForm = useAppSelector((state) => state.searchFlightForm.passenger) as any;
  const passengerArr = useAppSelector((state) => state.passenger.selectedPassengerArr);
  let totalApplicablePassengers = passengerArr.reduce((total, currPassenger) => total + (['adt', 'chd'].includes(currPassenger.type) ? 1 : 0), 0);
  if (!totalApplicablePassengers && passengerSearchForm) {
    totalApplicablePassengers = passengerSearchForm.adt + passengerSearchForm.chd;
  }

  const returnFlight = useAppSelector((state) => state.selectedFlight.returnFlight);
  const isReturn = !!returnFlight;

  const priceDiff = get(verifyFlightError, 'changed.priceDiff');
  const oldPrice = selectedFlight.totalFare || 0;
  const [newPriceState, setNewPriceState] = useState(0);
  let newPrice = newPriceState;

  const flightObj = JSON
    .parse(JSON
      .stringify((isReturn ? selectedFlight.returnFlight : selectedFlight.flight) || {})) as FlightData;
  let currDiscountAmount = 0;
  const handleCloseVerifyFlightDialog = () => {
    dispatch(setFlightErrorId(flightObj.idIndex));
    setVisibleVerifyFlightMessage.off();
    back();
  };

  if (!currFlight && !visibleVerifyFlightMessage && priceDiff && verifyFlightError && verifyFlightError.listFareData) {
    setVisibleVerifyFlightMessage.on();
    if (flightObj) {
      const fareData = verifyFlightError.listFareData[0];
      const schedule = flightObj?.route.schedules[0];
      if (schedule) {
        const {
          fareAdult, fareAdultOriginal, fareChild, fareChildOriginal, discountAmount, totalPriceAdultOriginal, totalPriceChildOriginal,
          totalPriceAdult, totalPriceChild,
        } = schedule;
        const {
          fareAdt, fareChd, taxAdt, feeAdt, discountAdt, serviceFeeAdt, feeChd, taxChd, discountChd, serviceFeeChd, fareInf, feeInf, serviceFeeInf, taxInf, totalPrice, totalNetPrice, discountInf,
        } = fareData;
        const adultDiff = fareAdult - fareAdultOriginal;
        const childDiff = fareChild - fareChildOriginal;
        const totalPriceAdultDiff = totalPriceAdult - totalPriceAdultOriginal;
        const totalPriceChildDiff = totalPriceChild - totalPriceChildOriginal;
        schedule.price = totalPrice;
        schedule.totalPrice = totalPrice;
        schedule.totalNetPrice = totalNetPrice;
        schedule.fareAdult = fareAdt + adultDiff;
        schedule.fareAdultOriginal = fareAdt;
        schedule.fareChild = fareChd + childDiff;
        schedule.fareChildOriginal = fareChd;
        schedule.fareInfant = fareInf;
        schedule.feeAdult = feeAdt;
        schedule.feeChild = feeChd;
        schedule.feeInfant = feeInf;
        schedule.serviceFeeAdult = serviceFeeAdt;
        schedule.serviceFeeChild = serviceFeeChd;
        schedule.serviceFeeInfant = serviceFeeInf;
        schedule.taxAdult = taxAdt;
        schedule.taxChild = taxChd;
        schedule.taxInfant = taxInf;
        schedule.totalPriceAdultOriginal = (fareAdt + taxAdt + feeAdt + discountAdt + serviceFeeAdt);
        schedule.totalPriceChildOriginal = (fareChd + taxChd + feeChd + discountChd + serviceFeeChd);
        schedule.totalPriceInfant = (fareInf + taxInf + feeInf + discountInf + serviceFeeInf);
        schedule.totalPriceAdult = schedule.totalPriceAdultOriginal + totalPriceAdultDiff;
        schedule.totalPriceChild = schedule.totalPriceChildOriginal + totalPriceChildDiff;
        currDiscountAmount += discountAmount;
        newPrice += (Object.keys(passengerSearchForm).reduce((total, currPassengerType) => {
          let newFare = 0;
          switch (currPassengerType) {
            case 'adt':
              newFare = schedule.totalPriceAdult;
              break;
            case 'chd':
              newFare = schedule.totalPriceChild;
              break;
            case 'inf':
              newFare = schedule.totalPriceInfant;
              break;
            default:
              break;
          }
          return total + (newFare * passengerSearchForm[currPassengerType]);
        }, 0));

        if (passengerSearchForm.chd && !fareChild) {
          setVerifyFlightErrorMsg('Chuyến không còn chỗ cho trẻ em, xin vui lòng tìm chuyến mới.');
        }

        if (verifyFlightError.listFareStatus) {
          const fareStatus = verifyFlightError.listFareStatus[0];
          const { listFlight, fareDataId } = fareStatus.fareData;

          if (listFlight && !isEmpty(listFlight)) {
            const updatedFlight = listFlight[0];
            const { fareClass, fareBasis, flightValue } = updatedFlight;
            schedule.fareClass = fareClass;
            schedule.fareBasis = fareBasis;
            schedule.flightValue = flightValue;

            const { idIndex } = flightObj;
            const idIndexArr = idIndex.split('|');
            idIndexArr[1] = fareClass;
            flightObj.idIndex = idIndexArr.join('|');

            const { listSegment } = updatedFlight;
            const updatedListSegment = flightObj.segments.map((segment) => {
              const matchingUpdatedSegment = listSegment
                .find((updatedSegment) => segment.startPoint === updatedSegment.startPoint && segment.endPoint === updatedSegment.endPoint);
              if (matchingUpdatedSegment) {
                segment.class = matchingUpdatedSegment.class;
                segment.fareBasis = matchingUpdatedSegment.fareBasis || segment.fareBasis;
              }
              return segment;
            });
            flightObj.segments = updatedListSegment;
            flightObj.fareDataId = fareDataId;
          }
        }

        flightObj.route.schedules[0] = schedule;
      }
      setCurrFlight(flightObj);
    }
    newPrice += (currDiscountAmount * totalApplicablePassengers);
    setNewPriceState(newPrice);
  }
  const applyPriceChange = (flight: any) => {
    if (flight) {
      if (!isReturn && selectedFlight.flight) {
        dispatch(addFlight(flight));
      } else if (isReturn && selectedFlight.returnFlight) {
        dispatch(addReturnFlight(flight));
      }
    }
    dispatch(resetGlobalAll());
    setVisibleVerifyFlightMessage.off();
  };

  return (
    <StyledDialog
      onClose={handleCloseVerifyFlightDialog}
      disableEscapeKeyDown
      open={visibleVerifyFlightMessage}
      PaperProps={{
        style: {
          paddingLeft: '20px',
          paddingRight: '20px',
        },
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onBackdropClick={handleCloseVerifyFlightDialog}
    >
      {verifyFlightErrorMsg
        ? (
          <StyledDialogContent>
            <StyledDialogTitle
              sx={{ textAlign: 'center' }}
              id="alert-dialog-title"
            >
              <Typography
                variant="subtitle2"
                color={style.color.black1}
              >
                Có lỗi xảy ra!
              </Typography>
            </StyledDialogTitle>
            <br />
            <DialogContentText>
              {!!verifyFlightErrorMsg && (
              <Typography variant="caption">
                {verifyFlightErrorMsg}
              </Typography>
              )}
              {!verifyFlightErrorMsg && (
              <Typography variant="caption">
                Kết nối đến hệ thống tạm thời gián đoạn. Quý khách vui lòng thử lại sau.
              </Typography>
              )}
            </DialogContentText>
            <StyledDialogActions style={{ marginTop: '20px' }}>
              <StyledButton
                style={{
                  borderColor: style.color.orange3,
                  color: style.color.orange3,
                }}
                variant="outlined"
                onClick={handleCloseVerifyFlightDialog}
              >
                Tìm vé mới
              </StyledButton>
            </StyledDialogActions>
          </StyledDialogContent>
        ) : null}
      {(!verifyFlightErrorMsg && priceDiff) ? (
        <StyledDialogContent>
          <StyledDialogTitle
            sx={{ textAlign: 'center' }}
            id="alert-dialog-title"
          >
            <Typography
              variant="subtitle2"
              color={style.color.black1}
            >
              Giá cước đã thay đổi
            </Typography>
          </StyledDialogTitle>
          <br />
          <DialogContentText>
            <Typography variant="caption">
              <span>
                Tổng giá cước đã được thay đổi từ
                {' '}
                <b>{toVND(oldPrice)}</b>
                {' '}
                thành
                {' '}
                <b>{toVND(newPrice)}</b>
                .
                <br />
                Bạn có muốn tiếp tục đặt vé?
              </span>
            </Typography>
          </DialogContentText>
          <StyledDialogActions style={{ marginTop: '20px' }}>
            <StyledButton
              style={{
                color: style.color.orange3,
              }}
              variant="text"
              onClick={handleCloseVerifyFlightDialog}
            >
              Trở lại
            </StyledButton>
            <StyledButton
              style={{
                background: style.color.orange3,
              }}
              variant="contained"
              onClick={() => applyPriceChange(currFlight)}
            >
              Tiếp tục
            </StyledButton>
          </StyledDialogActions>
        </StyledDialogContent>
      ) : null}
    </StyledDialog>
  );
};

const StyledDialogTitle = styled(DialogTitle)({
  fontWeight: 'bold',
  padding: 0,
});
const StyledButton = styled(Button)({
  ...style.text.content.bold,
  color: 'white',
  flex: '1 1 1px',
  padding: `${pr`12px`} ${pr`16px`}`,
});
const StyledDialogActions = styled(DialogActions)({
  display: 'flex',
  justifyContent: 'space-between',
  padding: 0,
});
const StyledDialogContent = styled(DialogContent)({
  padding: 0,
  marginTop: pr`16px`,
  marginBottom: '1.5em',
});
const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: ${style.color.white4};
    border: 1px solid ${style.color.white3};
    box-sizing: border-box;
    box-shadow: ${style.boxShadow.popupShadow};
    border-radius: 16px;
  }
`;

export default memo(VerifyFlightModal);
